/**
 * Accessibility Toolbar
 *
 * User-facing accessibility toolbar with expandable design.
 * Provides quick access to commonly-adjusted settings:
 * - Display Settings (theme, contrast, motion)
 * - Text Settings (font size, spacing, dyslexia font)
 * - Voice Settings (TTS speed and voice)
 * - Read Aloud toggle
 * - Keyboard Shortcuts
 *
 * Design Pattern: Popover-based quick settings (similar to AdminToolbar)
 * - Minimized: Floating button (bottom-left)
 * - Expanded: Full toolbar at bottom with icon buttons that open popovers
 */

import { useState, useEffect } from 'react'
import { Icon } from '@iconify/react'
import { Minimize2, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  getUserAccessibilitySettings,
  saveUserAccessibilitySettings,
  applyUserAccessibilitySettings,
  type UserAccessibilitySettings,
} from '@lina/accessibility/user'
import { KeyboardShortcutsModal } from './KeyboardShortcutsModal'
import { DisplaySettingsPopover } from './DisplaySettingsPopover'
import { TextSettingsPopover } from './TextSettingsPopover'
import { VoiceSettingsPopover } from './VoiceSettingsPopover'
import { cn } from '@/lib/utils'

interface AccessibilityToolbarProps {
  className?: string
}

export function AccessibilityToolbar({ className }: AccessibilityToolbarProps) {
  const [settings, setSettings] = useState<UserAccessibilitySettings>(() =>
    getUserAccessibilitySettings(),
  )
  const [isExpanded, setIsExpanded] = useState(false)
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false)

  // Update DOM when settings change
  useEffect(() => {
    applyUserAccessibilitySettings(settings)
    saveUserAccessibilitySettings(settings)
  }, [settings])

  const updateSetting = <K extends keyof UserAccessibilitySettings>(
    key: K,
    value: UserAccessibilitySettings[K],
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }

  // Don't render if user has hidden the button
  if (!settings.showAccessibilityButton) {
    return null
  }

  // Minimized state - just a floating button
  if (!isExpanded) {
    return (
      <div className={cn('fixed left-6 bottom-6 z-30', className)} data-skip-read-aloud="true">
        <Button
          onClick={() => setIsExpanded(true)}
          variant="outline"
          size="icon"
          className="h-12 w-12 bg-background/80 rounded-full transition-shadow btn-primary-glow"
          aria-label="Open accessibility settings"
          title="Accessibility settings"
        >
          <Icon icon="radix-icons:accessibility" className="h-6 w-6 text-primary" />
        </Button>
      </div>
    )
  }

  // Expanded toolbar
  return (
    <div
      className={cn('fixed bottom-0 left-0 right-0 z-40 glass-bordered border-t', className)}
      role="toolbar"
      aria-label="Accessibility controls"
      data-skip-read-aloud="true"
    >
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-6">
          {/* Left side - Quick Settings Popovers */}
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-muted-foreground">
              Accessibility Controls
            </span>

            <div className="h-6 w-px bg-border" />

            {/* Display Settings Popover (Theme, Contrast, Motion) */}
            <DisplaySettingsPopover>
              <Button
                variant="outline"
                size="sm"
                className="h-9 gap-2 px-3 transition-all shadow-sm"
                title="Display settings"
              >
                <Icon icon="radix-icons:half-2" className="h-4 w-4" />
                <span className="text-xs font-normal">Display</span>
                <ChevronUp className="h-3 w-3 opacity-50" />
              </Button>
            </DisplaySettingsPopover>

            {/* Text Settings Popover (Font Size, Line Spacing, Dyslexia Font) */}
            <TextSettingsPopover>
              <Button
                variant="outline"
                size="sm"
                className="h-9 gap-2 px-3 btn-accent-glow transition-all shadow-sm"
                title="Text settings"
              >
                <Icon icon="radix-icons:text" className="h-4 w-4" />
                <span className="text-xs font-normal">Text</span>
                <ChevronUp className="h-3 w-3 opacity-50" />
              </Button>
            </TextSettingsPopover>

            {/* Voice Settings Popover (Read Aloud Toggle + TTS Speed, Voice) */}
            <VoiceSettingsPopover
              showTTSToolbar={!!settings.showTTSToolbar}
              onToggleTTSToolbar={(checked) => updateSetting('showTTSToolbar', checked)}
            >
              <Button
                variant="outline"
                size="sm"
                className="h-9 gap-2 px-3 btn-secondary-glow transition-all shadow-sm"
                title="Voice settings"
              >
                <Icon icon="radix-icons:speaker-loud" className="h-4 w-4" />
                <span className="text-xs font-normal">Voice</span>
                <ChevronUp className="h-3 w-3 opacity-50" />
              </Button>
            </VoiceSettingsPopover>
          </div>

          {/* Center spacer */}
          <div className="flex-1" />

          {/* Right: Actions */}
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setShowKeyboardShortcuts(true)}
              variant="outline"
              size="sm"
              className="h-9 gap-2 px-3 btn-warm-glow transition-all shadow-sm"
              title="Keyboard shortcuts"
            >
              <Icon icon="radix-icons:keyboard" className="h-4 w-4" />
              <span className="text-xs font-normal">Shortcuts</span>
            </Button>

            <div className="h-6 w-px bg-border" />

            <Button
              onClick={() => setIsExpanded(false)}
              variant="ghost"
              size="icon"
              className="h-8 w-8 btn-primary-glow transition-all"
              aria-label="Minimize toolbar"
              title="Minimize toolbar"
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Keyboard Shortcuts Modal */}
        <KeyboardShortcutsModal
          open={showKeyboardShortcuts}
          onOpenChange={setShowKeyboardShortcuts}
        />
      </div>
    </div>
  )
}
